<template>
  <section class="bg-neutral-50 px-4 py-12 md:px-16">
    <div class="mx-auto flex w-full max-w-[1200px] flex-col gap-8">
      <div class="flex flex-col items-center gap-1 text-center">
        <p class="text-[11px] font-semibold uppercase leading-5 tracking-[1px] text-neutral-500 lg:text-[14px]">
          {{ overlineText }}
        </p>
        <h1 class="text-[32px] font-medium leading-[40px] tracking-tight text-neutral-900 lg:text-[36px] lg:leading-[44px]">
          {{ t('search.heading') }}
        </h1>
      </div>

      <!-- Search bar (echoes the active query; "Szukaj" re-runs on this page) -->
      <MagicSearchBar
        :placeholder-text="placeholderText"
        :button-label="buttonLabel"
        :initial-query="query"
        button-variant="outline"
        :site_context="site_context"
      />

      <!-- Doctor cards -->
      <div v-if="specialists.length" class="flex flex-col gap-8">
        <article
          v-for="specialist in paginatedSpecialists"
          :key="specialist.id"
          class="grid grid-cols-1 overflow-hidden rounded-[24px] bg-white shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)] lg:grid-cols-[1fr_1fr]"
        >
          <!-- Left: specialist info -->
          <div class="flex flex-col gap-3 border-b border-neutral-200 p-6 lg:border-b-0">
            <div v-if="specialist.specialisations?.length" class="flex flex-wrap items-start gap-2">
              <SpecialisationBadge
                v-for="spec in specialist.specialisations"
                :key="spec.id"
                :name="spec.name"
                :site-context="spec.site_context"
              />
            </div>

            <div class="flex items-start gap-3">
              <div v-if="specialist.photo_url" class="size-[96px] shrink-0 overflow-hidden rounded-md bg-neutral-200">
                <img
                  :src="specialist.photo_url"
                  :alt="specialist.full_name"
                  class="size-full object-cover"
                >
              </div>
              <div class="flex min-w-0 flex-1 flex-col items-start justify-center">
                <p v-if="specialist.title" class="text-[16px] leading-6 text-neutral-500">
                  {{ specialist.title }}
                </p>
                <p class="text-[20px] font-semibold leading-7 tracking-[-0.4px] text-neutral-900">
                  {{ specialist.full_name }}
                </p>
                <Link
                  :href="profileHref(specialist)"
                  class="mt-1 flex h-10 items-center gap-2 rounded-lg px-4 py-[10px] no-underline transition-colors hover:bg-neutral-100"
                >
                  <span class="text-[14px] font-medium leading-4" :style="{ color: themeColor }">
                    {{ t('specialists.show_profile') }}
                  </span>
                  <svg class="size-5 shrink-0" :style="{ color: themeColor }" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            <template v-if="hasAboutContent(specialist)">
              <hr class="border-t border-neutral-200">

              <template v-if="specialist.description">
                <h3 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
                  {{ t('specialists.about_heading') }}
                </h3>
                <ExpandableText :text="specialist.description" />
              </template>

              <template v-if="specialist.languages?.length">
                <hr
                  v-if="specialist.description"
                  class="border-t border-dashed border-neutral-200"
                >
                <h3 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
                  {{ t('specialists.languages') }}
                </h3>
                <LanguagesList :languages="specialist.languages" />
              </template>
            </template>
          </div>

          <!-- Right: specialist calendar (service select + available slots) -->
          <SpecialistBookingPanel
            :specialist-slug="specialist.slug"
            :bookable-services="specialist.bookable_services || []"
            :default-services="specialist.default_services || []"
            :show-header="false"
            flush
            class="border-neutral-200 lg:border-l"
          />
        </article>

        <FrontofficeListPagination
          v-model:current-page="currentPage"
          :total-pages="totalPages"
          :aria-label="t('specialists.search_pagination')"
        />
      </div>

      <!-- Empty state -->
      <div v-else class="rounded-[24px] bg-white py-16 text-center shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]">
        <h3 class="text-[16px] font-semibold text-neutral-900">
          {{ t('specialists.list_empty_title') }}
        </h3>
        <p class="mt-1 text-[14px] text-neutral-500">
          {{ query.trim() ? t('specialists.list_no_match', { query: query.trim() }) : t('specialists.list_empty') }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { themeColorHexFor } from '@/utils/siteThemeColors';
import { useClientPagination } from '@/utils/useClientPagination';
import SpecialisationBadge from '@/Shared/SpecialisationBadge.vue';
import MagicSearchBar from '@/Shared/MagicSearchBar.vue';
import SpecialistBookingPanel from '@/Shared/SpecialistBookingPanel.vue';
import LanguagesList from '@/Shared/LanguagesList.vue';
import ExpandableText from '@/Shared/ExpandableText.vue';
import FrontofficeListPagination from '@/Shared/FrontofficeListPagination.vue';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

const PER_PAGE = 5;

const props = defineProps({
  specialists:     { type: Array, default: () => [] },
  query:           { type: String, default: '' },
  placeholderText: { type: String, default: 'Wybierz lub wpisz' },
  buttonLabel:     { type: String, default: 'Szukaj' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context:    { type: String, default: null },
});

const themeColor = computed(() => themeColorHexFor(props.site_context));

const overlineText = computed(() => {
  const key = `sites.${props.site_context || 'code_medical_center'}.overline`;
  return t(key);
});

const specialists = computed(() => props.specialists);

const {
  currentPage,
  totalPages,
  paginatedItems: paginatedSpecialists,
} = useClientPagination(specialists, PER_PAGE, {
  resetWatchSources: [() => props.query],
});

function profileHref(specialist) {
  return `/specjalisci/${specialist.slug}`;
}

function hasAboutContent(specialist) {
  return Boolean(specialist.description || specialist.languages?.length);
}
</script>
